// src/components/BucketingModal.tsx

import React, { useEffect, useCallback, useState } from 'react';
import {
  BucketingState,
  BucketType,
  BucketSummary,
  recordBucketDecision,
  undoBucketDecision,
  isBucketingComplete,
  getBucketSummary,
} from '../utils/bucketingState';
import './BucketingModal.css';

interface TaskForBucketing {
  id: string;
  title: string;
  categoryName: string;
  createdAt: number;
  description?: string;
}

interface BucketingModalProps {
  opened: boolean;
  onClose: () => void;
  state: BucketingState;
  tasks: TaskForBucketing[];
  onStateChange: (state: BucketingState) => void;
  onComplete: (state: BucketingState) => void;
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);

  if (weeks > 0) return `${weeks}w ago`;
  if (days > 0) return `${days}d ago`;
  return 'today';
}

export const BucketingModal: React.FC<BucketingModalProps> = ({
  opened,
  onClose,
  state,
  tasks,
  onStateChange,
  onComplete,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const isComplete = isBucketingComplete(state);
  const currentTask = !isComplete ? tasks.find(t => t.id === state.allTasks[state.currentIndex]) : null;
  const summary = isComplete ? getBucketSummary(state) : null;
  const canUndo = state.history.length > 0;
  const hasDescription = currentTask?.description && currentTask.description.trim().length > 0;

  const handleBucket = useCallback((bucket: BucketType, willDoToday: boolean) => {
    const newState = recordBucketDecision(state, bucket, willDoToday);
    onStateChange(newState);
    setIsDescriptionExpanded(false); // Reset expansion for next task
  }, [state, onStateChange]);

  const handleUndo = useCallback(() => {
    const newState = undoBucketDecision(state);
    onStateChange(newState);
  }, [state, onStateChange]);

  const handleDone = useCallback(() => {
    onComplete(state);
    onClose();
  }, [state, onComplete, onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!opened || isComplete) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case '1':
          handleBucket('high', true);
          break;
        case '2':
          handleBucket('high', false);
          break;
        case '3':
          handleBucket('medium', false);
          break;
        case '4':
          handleBucket('low', false);
          break;
        case '5':
          handleBucket('done', false);
          break;
        case '6':
        case 'x':
        case 'X':
          handleBucket('delete', false);
          break;
        case 'ArrowDown':
          if (canUndo) handleUndo();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [opened, isComplete, handleBucket, handleUndo, canUndo, onClose]);

  if (!opened) return null;

  const progress = (state.currentIndex / state.allTasks.length) * 100;

  // Completion screen after bucketing completes
  if (isComplete && summary) {
    return (
      <div className="bucketing-modal-overlay" onClick={onClose}>
        <div className="bucketing-modal-content" onClick={e => e.stopPropagation()}>
          <div className="bucketing-complete">
            <div className="bucketing-complete-icon">✅</div>
            <div className="bucketing-complete-title">Bucketing Complete!</div>
            <BucketSummaryDisplay summary={summary} />
            <button className="bucketing-done-button" onClick={handleDone}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main bucketing UI
  return (
    <div className="bucketing-modal-overlay" onClick={onClose}>
      <div className="bucketing-modal-content" onClick={e => e.stopPropagation()}>
        <div className="bucketing-progress">
          <div className="bucketing-progress-text">
            {state.currentIndex + 1} of {state.allTasks.length}
          </div>
          <div className="bucketing-progress-bar">
            <div className="bucketing-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {currentTask && (
          <div className="bucketing-task-card">
            <div className="bucketing-task-title">{currentTask.title}</div>
            <div className="bucketing-task-meta">
              {currentTask.categoryName} • {formatRelativeTime(currentTask.createdAt)}
            </div>
            {hasDescription && (
              <div className="bucketing-task-description-section">
                <button
                  className="bucketing-description-toggle"
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                >
                  <span className={`bucketing-description-arrow ${isDescriptionExpanded ? 'expanded' : ''}`}>▶</span>
                  {isDescriptionExpanded ? 'Hide details' : 'Show details'}
                </button>
                {isDescriptionExpanded && (
                  <div
                    className="bucketing-task-description"
                    dangerouslySetInnerHTML={{ __html: currentTask.description || '' }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <div className="bucketing-buttons">
          <button
            className="bucketing-button bucketing-button-today"
            onClick={() => handleBucket('high', true)}
          >
            <span className="bucketing-button-shortcut">1</span>
            🔥 Today
          </button>
          <button
            className="bucketing-button bucketing-button-high"
            onClick={() => handleBucket('high', false)}
          >
            <span className="bucketing-button-shortcut">2</span>
            High
          </button>
          <button
            className="bucketing-button bucketing-button-medium"
            onClick={() => handleBucket('medium', false)}
          >
            <span className="bucketing-button-shortcut">3</span>
            Medium
          </button>
          <button
            className="bucketing-button bucketing-button-low"
            onClick={() => handleBucket('low', false)}
          >
            <span className="bucketing-button-shortcut">4</span>
            Low
          </button>
        </div>

        <div className="bucketing-secondary-buttons">
          <button
            className="bucketing-secondary-button bucketing-button-done"
            onClick={() => handleBucket('done', false)}
          >
            <span className="bucketing-button-shortcut">5</span>
            ✓ Done
          </button>
          <button
            className="bucketing-secondary-button bucketing-button-delete"
            onClick={() => handleBucket('delete', false)}
          >
            <span className="bucketing-button-shortcut">6</span>
            ✕ Delete
          </button>
        </div>

        <div className="bucketing-keyboard-hint">
          Press 1-6 or click • ↓ Undo
        </div>

        <div className="bucketing-actions">
          <button
            className="bucketing-undo-button"
            onClick={handleUndo}
            disabled={!canUndo}
          >
            ↩ Undo
          </button>
          <button className="bucketing-close-button" onClick={onClose}>
            Save & Exit
          </button>
        </div>
      </div>
    </div>
  );
};

const BucketSummaryDisplay: React.FC<{ summary: BucketSummary }> = ({ summary }) => (
  <div className="bucketing-summary">
    <div className="bucketing-summary-item">
      <div className="bucketing-summary-count today">{summary.today}</div>
      <div className="bucketing-summary-label">🔥 Today</div>
    </div>
    <div className="bucketing-summary-item">
      <div className="bucketing-summary-count high">{summary.high}</div>
      <div className="bucketing-summary-label">High</div>
    </div>
    <div className="bucketing-summary-item">
      <div className="bucketing-summary-count medium">{summary.medium}</div>
      <div className="bucketing-summary-label">Medium</div>
    </div>
    <div className="bucketing-summary-item">
      <div className="bucketing-summary-count low">{summary.low}</div>
      <div className="bucketing-summary-label">Low</div>
    </div>
    {summary.done > 0 && (
      <div className="bucketing-summary-item">
        <div className="bucketing-summary-count done">{summary.done}</div>
        <div className="bucketing-summary-label">✓ Done</div>
      </div>
    )}
    {summary.deleted > 0 && (
      <div className="bucketing-summary-item">
        <div className="bucketing-summary-count deleted">{summary.deleted}</div>
        <div className="bucketing-summary-label">✕ Delete</div>
      </div>
    )}
  </div>
);
